import { useState, useEffect } from 'react';
import { Link, RouteComponentProps, useNavigate } from '@reach/router';
import { styled, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import { usePolicyStreamGoogleAnalytics } from '../../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../../hooks/usePolicyStreamGoogleAnalytics/types';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Slider, { sliderClasses } from '@mui/material/Slider';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import GppGoodIcon from '@mui/icons-material/GppGood';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { graphql } from '../../../gql';
import { useMutation } from '@apollo/client';
import { MakePolicyStreamPlanCallMutation } from '../../../gql/graphql';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { isNotNullish } from '../../../util';
import { StoreState } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { policyStreamPaths } from '../policyStreamConstants';
import { ClickHandIcon } from '../components/ClickHandIcon';
import {
  getRecommendation,
  // DEFAULT_COVERS_SPOUSE,
  DEFAULT_POLICY_LENGTH_YEARS,
  DEFAULT_COVERAGE_AMOUNT_DOLLARS,
  getSex,
  getIsSmoker,
  getBirthDate,
  getProvinceCode,
  setSelectedPlan,
  getPlanPaymentFrequency,
} from '../../../store/policystreamStore';
import {
  useSetPolicyStreamSaveButtonVisibility,
  useSetStepStatus,
  useSubmitPolicyStreamQuote,
} from '../../../hooks';
import { useEnsureFormPresent } from '../../../hooks/useEnsureFormPresent';
import { POLICYSTREAM_FORMS } from '../constants';
import { formatAsDollar } from '../utility';
import { PolicyStreamLayout } from '../components';
import { MIN_AGE, MAX_AGE } from '../constants';
import { PolicyStreamInvalidAge } from '../components/PolicyStreamInvalidAge';
import {
  getPolicyStreamClientAgeYears,
  getPolicyStreamRecommendationSelection,
} from '../../../store/utility/policyStreamUtility';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { PolicyStreamContactBroker } from '../components/PolicyStreamContactBroker';
import { DisplayPolicyStreamPlan } from './PolicyStreamDisplayPlan';
import { PolicyStreamCard } from '../components';
import { PolicyStreamFaq } from '../components/PolicyStreamFaq';
import { grey } from '@mui/material/colors';
import {
  MissingInformation,
  useMissingBasicInfoGuard,
} from '../components/MissingInformation';
import Bugsnag from '@bugsnag/browser';
import { errorify } from '@calefy-inc/utility';

const COVERAGE_AMOUNT_SLIDER_MIN = 100_000;
const COVERAGE_AMOUNT_SLIDER_MAX = 5_000_000;
const COVERAGE_AMOUNT_SLIDER_STEP = 50_000;

const PLANS_PER_CHUNK = 3; // number of plans to display together

export const PolicyStreamPlanSelectionPage = (_props: RouteComponentProps) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  useScrollToTopOnInitialMount();
  useSetPolicyStreamSaveButtonVisibility(true);
  // preload for the next page
  useEnsureFormPresent({ internalName: POLICYSTREAM_FORMS.basicClientInfo });

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.PlanSelection,
      EventActions.PageLoad,
      'PolicyStream Plan Selection Page',
    );
  }, [myAnalytics]);
  const setStepStatus = useSetStepStatus('Plan Selection');
  const clientAge = useSelector(getPolicyStreamClientAgeYears);

  useEffect(() => {
    setStepStatus('IN_PROGRESS');
  }, []);

  return (
    <PolicyStreamLayout>
      <Typography
        component='h1'
        variant='h4'
        sx={{ fontWeight: 'bold', textAlign: 'left' }}
      >
        Customize your Life Insurance Plan
      </Typography>
      <PolicyStreamPlanSelectionPageContainer>
        {clientAge !== null && (clientAge < MIN_AGE || clientAge > MAX_AGE) ? (
          <PolicyStreamInvalidAge />
        ) : (
          <>
            <PolicyStreamPlanInformation />
            <PrivacyReassuranceComponent />
            <TakeQuizComponent />
            <PolicyStreamFaq />
          </>
        )}
      </PolicyStreamPlanSelectionPageContainer>
    </PolicyStreamLayout>
  );
};

const OnlineComponent = () => {
  return (
    <Typography sx={{ color: grey[700], mt: 2, textAlign: 'center' }}>
      100% Online. No pressure. Takes just 10 minutes.
    </Typography>
  );
};

const PolicyStreamPlanSelectionPageContainer = styled('div')(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
  };
});

const policyLengths = [10, 15, 20, 25, 30];
// idea here is to have them stacked on top of each other
const PolicyLengthLabel = styled('span')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    marginBottom: theme.spacing(1),
  };
});

const policyLengthOptions = policyLengths.map((year) => {
  return {
    label: (
      <PolicyLengthLabel>
        <span>{year}</span>
        <span>Years</span>
      </PolicyLengthLabel>
    ),
    value: year,
  };
});

interface PolicyStreamPlanInformationProps {}
const PolicyStreamPlanInformation = (
  _props: PolicyStreamPlanInformationProps,
) => {
  // required information
  const provinceCode = useSelector(getProvinceCode);
  const sex = useSelector(getSex);
  const isSmoker = useSelector(getIsSmoker);
  const birthDate = useSelector(getBirthDate);

  const missingInfo = useMissingBasicInfoGuard();

  const recommendation = useSelector(getRecommendation);
  const [displayPolicyLength, setDisplayPolicyLength] = useState<number>(
    recommendation?.policyLengthYears || DEFAULT_POLICY_LENGTH_YEARS,
  );
  const [policyLength, setPolicyLength] = useState<number>(
    recommendation?.policyLengthYears || DEFAULT_POLICY_LENGTH_YEARS,
  );
  const [coverageAmount, setCoverageAmount] = useState<number>(
    recommendation?.coverageAmountDollars
      ? getPolicyStreamRecommendationSelection(
          recommendation.coverageAmountDollars,
        )
      : DEFAULT_COVERAGE_AMOUNT_DOLLARS,
  );
  const [committedCoverageAmount, setCommittedCoverageAmount] =
    useState<number>(
      recommendation?.coverageAmountDollars
        ? getPolicyStreamRecommendationSelection(
            recommendation.coverageAmountDollars,
          )
        : DEFAULT_COVERAGE_AMOUNT_DOLLARS,
    );

  // update the selected value whenever the recommendation changes
  useEffect(() => {
    if (recommendation?.policyLengthYears) {
      setPolicyLength(recommendation?.policyLengthYears);
    }
    if (recommendation?.coverageAmountDollars) {
      setCoverageAmount(
        getPolicyStreamRecommendationSelection(
          recommendation.coverageAmountDollars,
        ),
      );
    }
  }, [recommendation]);

  if (missingInfo) {
    return (
      <MissingInformation
        missing={missingInfo.missing}
        redirectURL={policyStreamPaths.baseInformation.url}
        stepName='Plan Selection'
      />
    );
  }

  return (
    <PolicyStreamPlanInformationContainer>
      <PolicyStreamRatingInformationContainer>
        <SubtitleContainer>
          <Typography
            variant='subtitle1'
            component='h2'
            sx={{ fontWeight: 500 }}
          >
            Get quotes instantly. Choose your coverage. Apply online in minutes.
          </Typography>
        </SubtitleContainer>
        <RatingInformationFormControl>
          <RatingInformationFormElementCard>
            <CoverageAmountContainer>
              <CoverageAmountLabelContainer>
                <FormLabel htmlFor='coverageAmount'>Coverage Amount</FormLabel>
                <CoverageAmountTextField
                  value={formatAsDollar(coverageAmount)}
                  onChange={(e) => {
                    // Remove dollar sign and commas, parse as number
                    const cleanValue = e.target.value.replace(/[$,]/g, '');
                    const asNumber = Number(cleanValue);
                    if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
                      setCoverageAmount(asNumber);
                    }
                  }}
                  onBlur={(_e) => {
                    // Respect min/max limits
                    const clampedAmount = Math.min(
                      COVERAGE_AMOUNT_SLIDER_MAX,
                      Math.max(COVERAGE_AMOUNT_SLIDER_MIN, coverageAmount),
                    );
                    setCoverageAmount(clampedAmount);
                    setCommittedCoverageAmount(clampedAmount);
                  }}
                  variant='standard'
                  size='small'
                />
              </CoverageAmountLabelContainer>
              <Slider
                value={coverageAmount}
                min={COVERAGE_AMOUNT_SLIDER_MIN}
                max={COVERAGE_AMOUNT_SLIDER_MAX}
                step={COVERAGE_AMOUNT_SLIDER_STEP}
                onChangeCommitted={(_e, amount) => {
                  // const amount = e.target?.value;
                  const asNumber = Number(amount);
                  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
                    setCoverageAmount(asNumber);
                    // this is what was added
                    setCommittedCoverageAmount(asNumber);
                  }
                }}
                onChange={(e) => {
                  // @ts-expect-error
                  const amount = e.target?.value;
                  const asNumber = Number(amount);
                  if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
                    setCoverageAmount(asNumber);
                  }
                }}
              />
              <CoverageMinMaxContainer>
                {[COVERAGE_AMOUNT_SLIDER_MIN, COVERAGE_AMOUNT_SLIDER_MAX].map(
                  (amount) => {
                    return <Typography>{formatAsDollar(amount)}</Typography>;
                  },
                )}
              </CoverageMinMaxContainer>
            </CoverageAmountContainer>
          </RatingInformationFormElementCard>
          <RatingInformationFormElementCard>
            <PolicyLengthLabelContainer>
              <FormLabel htmlFor='policyLength'>Select Policy Length</FormLabel>
              <Typography sx={{ fontWeight: 'bold' }}>
                {displayPolicyLength} Years
              </Typography>
            </PolicyLengthLabelContainer>
            <CoverageLengthSlider
              id='policyLength'
              min={Math.min(...policyLengths) - 5}
              max={Math.max(...policyLengths) + 5}
              step={null}
              value={displayPolicyLength}
              marks={policyLengthOptions}
              track={false}
              onChange={(_e, value) => {
                if (!Array.isArray(value)) {
                  setDisplayPolicyLength(value);
                }
              }}
              onChangeCommitted={(_e, newValue) => {
                if (newValue !== null) {
                  const matching = policyLengthOptions.find(
                    (option) => option.value === newValue,
                  );
                  if (matching) {
                    setPolicyLength(Number(matching.value));
                  }
                }
              }}
            />
          </RatingInformationFormElementCard>
        </RatingInformationFormControl>
      </PolicyStreamRatingInformationContainer>
      <PolicyStreamPackages
        policyLength={policyLength}
        coverageAmount={committedCoverageAmount}
        // coversSpouse={coversSpouse}
        sex={sex}
        provinceCode={provinceCode}
        isSmoker={isSmoker}
        birthDate={birthDate}
      />
    </PolicyStreamPlanInformationContainer>
  );
};

const PrivacyReassuranceComponent = () => {
  return (
    <PrivacyReassuranceContainer>
      <GppGoodIcon style={{ fontSize: '3rem' }} />
      <PrivacyReassuranceText />
    </PrivacyReassuranceContainer>
  );
};

const PrivacyReassuranceText = () => {
  return (
    <PrivacyReassuranceTextContainer>
      <Typography variant='h5'>Private. Secure. No Obligation.</Typography>
      <Typography>
        Your information is protected by advanced encryption. This site is
        operated by licensed life insurance professionals. Your data is never
        shared without your consent.
      </Typography>
    </PrivacyReassuranceTextContainer>
  );
};

const PrivacyReassuranceTextContainer = styled('div')(({ theme }) => {
  return { marginTop: theme.spacing(2) };
});

const PrivacyReassuranceContainer = styled(PolicyStreamCard)(({ theme }) => {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    padding: theme.spacing(1),
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  };
});

const CoverageLengthSlider = styled(Slider)(({ theme }) => {
  return {
    [`& .${sliderClasses.mark}`]: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      transform: 'translateX(-50%) translateY(-50%)',
    },
    [`& .${sliderClasses.thumb}`]: {
      width: 20,
      height: 20,
      border: `3px solid ${theme.palette.primary.main}`,
      backgroundColor: 'white',
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  };
});

const CoverageMinMaxContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',

  alignItems: 'center',
  justifyContent: 'space-between',
});

const CoverageAmountLabelContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const CoverageAmountContainer = styled('div')({});

const PolicyLengthLabelContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const RatingInformationFormElementCard = styled(PolicyStreamCard)(
  ({ theme }) => {
    return {
      width: '100%',
      padding: theme.spacing(1),
    };
  },
);

const RatingInformationFormControl = styled(FormControl)(({ theme }) => {
  return {
    width: '100%',
    display: 'grid',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    margin: theme.spacing(1, 0),
  };
});

const PolicyStreamRatingInformationContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: 4,
});

const PolicyStreamPlanInformationContainer = styled('div')(() => {
  return {
    width: '100%',
    maxWidth: '1000px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  };
});

export type PolicyStreamPlan = NonNullable<
  ArrayElement<
    NonNullable<MakePolicyStreamPlanCallMutation['makePolicyStreamPlanCall']>
  >
>;

interface PolicyStreamPackagesProps {
  policyLength: number;
  coverageAmount: number;
  // coversSpouse: boolean;
  sex: string | null;
  birthDate: string | null;
  provinceCode: string | null;
  isSmoker: boolean | null;
}
const PolicyStreamPackages = ({
  policyLength,
  coverageAmount,
  sex,
  birthDate,
  provinceCode,
  isSmoker,
}: PolicyStreamPackagesProps) => {
  const [applicationInternalName, setApplicationInternalName] = useState<
    string | undefined
  >();
  const [plans, setPlans] = useState<Array<PolicyStreamPlan>>([]);
  const [selectedPlan, setSelectedPlan] = useState<PolicyStreamPlan>(null);
  const theme = useTheme();
  const [numChunksToDisplay, setNumChunksToDisplay] = useState<number>(1); // we're displaying the plans in groups; this sets how many we are currently displaying
  useEnsureFormPresent({ internalName: applicationInternalName });
  const [makePolicyStreamPlanCall, { loading, error, data }] = useMutation(
    MAKE_POLICY_STREAM_PLAN_CALL,
    {
      onCompleted: (data) => {
        setPlans(
          data.makePolicyStreamPlanCall?.plans
            ? data.makePolicyStreamPlanCall.plans
                .filter(isNotNullish)
                .map((plan) => {
                  return {
                    ...plan,
                    premiumAnnualDollars: plan.premiumAnnualDollars
                      ? plan.premiumAnnualDollars.replace(/[$,]/g, '')
                      : '',
                    premiumMonthlyDollars: plan.premiumMonthlyDollars
                      ? plan.premiumMonthlyDollars.replace(/[$,]/g, '')
                      : '',
                    premiumQuarterlyDollars: plan.premiumQuarterlyDollars
                      ? plan.premiumQuarterlyDollars.replace(/[$,]/g, '')
                      : '',
                  };
                })
                .sort((a, b) => {
                  const firstAsNumber = Number(a.premiumAnnualDollars);
                  const secondAsNumber = Number(b.premiumAnnualDollars);
                  return firstAsNumber - secondAsNumber;
                })
            : [],
        );
      },
    },
  );

  useEffect(() => {
    const formattedProvince = provinceCode ? provinceCode : null;
    const formattedSex = sex === 'male' ? 'M' : sex === 'female' ? 'F' : null;
    const formattedDateOfBirth = birthDate;
    const formattedIsSmoker = typeof isSmoker === 'boolean' ? isSmoker : null;
    const requiredAttrs = [
      formattedProvince,
      formattedSex,
      formattedDateOfBirth,
      formattedIsSmoker,
    ];
    if (requiredAttrs.every((requiredAttr) => requiredAttr !== null)) {
      makePolicyStreamPlanCall({
        variables: {
          // @ts-expect-error
          provinceCode: formattedProvince,
          policyLengthYears: policyLength,
          coverageAmountDollars: coverageAmount,
          // @ts-expect-error
          sex: formattedSex,
          // @ts-expect-error
          isSmoker: formattedIsSmoker,
          birthDate: formattedDateOfBirth,
        },
      });
    }
  }, [policyLength, coverageAmount, sex, isSmoker, birthDate]);

  // if there's only one plan, select it
  useEffect(() => {
    if (plans.length === 1) {
      setSelectedPlan(plans[0]);
    }
  }, [plans]);

  // this is dumb right now. In the future we'll have different forms for different carriers / underwriters.
  useEffect(() => {
    if (selectedPlan) {
      setApplicationInternalName(POLICYSTREAM_FORMS.applicationForm);
    }
  }, [selectedPlan]);

  if (
    [sex, provinceCode, birthDate, isSmoker].some(
      (requiredInfo) => requiredInfo === null,
    )
  ) {
    return null;
  }

  return (
    <PolicyStreamPackagesContainer>
      {!loading && !error
        ? plans.map((plan, index) => {
            const shouldShow =
              Math.floor(index / PLANS_PER_CHUNK) < numChunksToDisplay;
            return (
              <Collapse
                in={shouldShow}
                collapsedSize={0}
                key={plan.id}
                sx={{
                  margin: 0,
                  padding: 0,
                  marginBottom: !shouldShow ? 0 : theme.spacing(1),
                }}
              >
                <DisplayPolicyStreamPlan
                  key={plan.id}
                  plan={plan}
                  plans={plans}
                  selected={selectedPlan && selectedPlan.id === plan.id}
                  select={() => setSelectedPlan(plan)}
                />
              </Collapse>
            );
          })
        : null}
      <SeeMoreSeeLessButtonsContainer>
        <Collapse
          in={!loading && numChunksToDisplay > 1}
          orientation='horizontal'
          collapsedSize={0}
        >
          <Button
            sx={{
              textDecoration: 'underline',
              color: 'revert',
            }}
            onClick={() =>
              setNumChunksToDisplay((oldChunks) => Math.max(oldChunks - 1, 1))
            }
            startIcon={<KeyboardArrowUpIcon />}
          >
            See Less
          </Button>
        </Collapse>
        <Collapse
          in={
            !loading &&
            plans.length > 0 &&
            plans.length > PLANS_PER_CHUNK * numChunksToDisplay
          }
          orientation='horizontal'
          collapsedSize={0}
        >
          <Button
            sx={{
              marginTop: 0,
              textDecoration: 'underline',
              color: 'revert',
            }}
            onClick={() => {
              setNumChunksToDisplay((oldChunks) => oldChunks + 1);
            }}
            endIcon={<KeyboardArrowDownIcon />}
          >
            See More
          </Button>
        </Collapse>
      </SeeMoreSeeLessButtonsContainer>
      {loading ? (
        <PlanLoadingContainer>
          <Typography>Getting plans now...</Typography>
          <CircularProgress />
        </PlanLoadingContainer>
      ) : error ? (
        <DisplayGetPlanError error={error} />
      ) : data ? (
        plans.length === 0 ? (
          <NoPlansComponent />
        ) : plans.length > 0 ? (
          <SelectPlanButtonContainer>
            <SelectPlanButton plan={selectedPlan} />
            <OnlineComponent />
          </SelectPlanButtonContainer>
        ) : null
      ) : null}
    </PolicyStreamPackagesContainer>
  );
};

const SeeMoreSeeLessButtonsContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    margin: theme.spacing(0, 2, 2, 2),
    paddingTop: 0,
  };
});

const SelectPlanButtonContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch',
});

const PlanLoadingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const CoverageAmountTextField = styled(TextField)({
  fontWeight: 'bold',
  '& .MuiInputBase-input': {
    fontWeight: 'bold',
    textAlign: 'right',
  },
});

interface DisplayGetPlanErrorProps {
  error: Error;
}
const DisplayGetPlanError = ({ error }: DisplayGetPlanErrorProps) => {
  useEffect(() => {
    if (error) {
      Bugsnag.notify(errorify(error));
    }
  }, [error]);

  if (
    error.message.match(/unable to retrieve plans for this coverage amount/i)
  ) {
    return (
      <Typography>
        It looks like there are currently no plans meeting your requirements. To
        get coverage, please contact your agent directly at{' '}
        <PolicyStreamContactBroker />.
      </Typography>
    );
  }
  return (
    <>
      <Typography>
        Oops! It looks like there was a problem getting plans. Please save the
        application and try refreshing the page. If this error persists, please
        contact your agent directly at <PolicyStreamContactBroker />.
      </Typography>
    </>
  );
};

const NoPlansComponent = () => {
  return (
    <Typography>
      No plans matching your needs are available at the moment! Please contact
      your broker directly at <PolicyStreamContactBroker />; they may be able to
      create a plan suited to your needs.
    </Typography>
  );
};

const getSelectedPlan = (store: StoreState) => {
  return store.policystreamStore.selectedPlan;
};
interface SelectPlanButtonProps {
  plan: PolicyStreamPlan | null;
}
const SelectPlanButton = ({ plan }: SelectPlanButtonProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const planInStore = useSelector(getSelectedPlan);
  const planPaymentFrequency = useSelector(getPlanPaymentFrequency);
  const [clicked, setClicked] = useState<boolean>(false);
  const submitQuote = useSubmitPolicyStreamQuote();
  const setStepStatus = useSetStepStatus('Plan Selection');

  useEffect(() => {
    return () => setClicked(false);
  }, []);

  // navigate when the plan is in the store
  useEffect(() => {
    if (clicked && planInStore) {
      submitQuote({ status: 'IN_PROGRESS' });
      const url = policyStreamPaths.postPlanSelectionInformation.url;
      navigate(url);
    }
  }, [clicked, planInStore]);

  return (
    <Button
      variant='contained'
      disabled={!plan || clicked || !planPaymentFrequency}
      onClick={() => {
        setClicked(true);
        dispatch(setSelectedPlan(plan));
        setStepStatus('COMPLETED');
      }}
    >
      Get Covered Now <ArrowOutwardIcon />
    </Button>
  );
};

const PolicyStreamPackagesContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // gap: 3,
});

const SubtitleContainer = styled('div')(({ theme }) => {
  return {
    alignItems: 'start',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  };
});

const TakeQuizComponent = () => {
  const answeredNeedsAnalysis = useSelector((state: StoreState) =>
    Object.values(state.policystreamStore.formAnswers).some(
      (form) => form.form.internalName === POLICYSTREAM_FORMS.needsAnalysis,
    ),
  );
  const theme = useTheme();

  if (answeredNeedsAnalysis) {
    return null;
  }
  return (
    <TakeQuizContainer>
      <ClickHandIcon colour={theme.palette.secondary.contrastText} />
      <TakeQuizTextContainer>
        <Typography variant='h5'>
          Uncertain about coverage? Take our quiz.
        </Typography>
        <Typography variant='subtitle2'>
          Take our quiz and find out exactly how much we recommend!
        </Typography>
      </TakeQuizTextContainer>
      <Button
        variant='contained'
        component={Link}
        to={policyStreamPaths.personalize.url}
      >
        Take the Quiz
      </Button>
    </TakeQuizContainer>
  );
};

const TakeQuizTextContainer = styled('div')({});
const TakeQuizContainer = styled(PolicyStreamCard)(({ theme }) => {
  return {
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    padding: theme.spacing(1),
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  };
});

const MAKE_POLICY_STREAM_PLAN_CALL = graphql(`
  mutation makePolicyStreamPlanCall(
    $policyLengthYears: Int!
    $coverageAmountDollars: Int!
    $isSmoker: Boolean!
    $birthDate: Date!
    $sex: String!
    $provinceCode: String!
  ) {
    makePolicyStreamPlanCall(
      policyLengthYears: $policyLengthYears
      coverageAmountDollars: $coverageAmountDollars
      isSmoker: $isSmoker
      birthDate: $birthDate
      sex: $sex
      provinceCode: $provinceCode
    ) {
      plans {
        id
        insurer {
          displayName
          logoUrl
        }
        premiumMonthlyDollars
        premiumAnnualDollars
        premiumQuarterlyDollars
        markdownDetails
        termLength
        coverageAmountDollars
      }
    }
  }
`);
